import { openDB, type DBSchema, type IDBPDatabase } from 'idb';

interface StoredDocumentFile {
  documentId: string;
  blob: Blob;
  fileName: string;
  mimeType: string;
  updatedAt: string;
}

interface MasarArchiveDB extends DBSchema {
  files: {
    key: string;
    value: StoredDocumentFile;
    indexes: { 'by-updated': string };
  };
  drafts: {
    key: string;
    value: { id: string; payload: unknown; updatedAt: string };
  };
}

let dbPromise: Promise<IDBPDatabase<MasarArchiveDB>> | null = null;

function getDb(): Promise<IDBPDatabase<MasarArchiveDB>> {
  if (!dbPromise) {
    dbPromise = openDB<MasarArchiveDB>('masar-pharmacy-archive', 1, {
      upgrade(db) {
        if (!db.objectStoreNames.contains('files')) {
          const files = db.createObjectStore('files', { keyPath: 'documentId' });
          files.createIndex('by-updated', 'updatedAt');
        }
        if (!db.objectStoreNames.contains('drafts')) {
          db.createObjectStore('drafts', { keyPath: 'id' });
        }
      },
    });
  }
  return dbPromise;
}

export async function saveDocumentFile(
  documentId: string,
  blob: Blob,
  fileName: string,
  mimeType = blob.type || 'application/octet-stream',
): Promise<void> {
  const db = await getDb();
  await db.put('files', {
    documentId,
    blob,
    fileName,
    mimeType,
    updatedAt: new Date().toISOString(),
  });
}

export async function getDocumentFile(documentId: string): Promise<StoredDocumentFile | undefined> {
  const db = await getDb();
  return db.get('files', documentId);
}

export async function deleteDocumentFile(documentId: string): Promise<void> {
  const db = await getDb();
  await db.delete('files', documentId);
}

export async function createDocumentObjectUrl(documentId: string): Promise<string | null> {
  const entry = await getDocumentFile(documentId);
  return entry ? URL.createObjectURL(entry.blob) : null;
}

export async function downloadDocumentFile(documentId: string): Promise<boolean> {
  const entry = await getDocumentFile(documentId);
  if (!entry) return false;
  const url = URL.createObjectURL(entry.blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = entry.fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
  return true;
}

export async function saveDraft(id: string, payload: unknown): Promise<void> {
  const db = await getDb();
  await db.put('drafts', { id, payload, updatedAt: new Date().toISOString() });
}

export async function loadDraft<T>(id: string): Promise<T | null> {
  const db = await getDb();
  const draft = await db.get('drafts', id);
  return (draft?.payload as T | undefined) ?? null;
}

export async function deleteDraft(id: string): Promise<void> {
  const db = await getDb();
  await db.delete('drafts', id);
}

export async function getLocalStorageUsage(): Promise<number> {
  const db = await getDb();
  const files = await db.getAll('files');
  return files.reduce((sum, file) => sum + file.blob.size, 0);
}
